import React, { useState } from 'react'
import { LocalizationContextType } from '../../shared/contexts'
import { formatTenantDateTime } from '../../../utils/formatTenantDateTime'
import { TIME_SOURCE, timeSourceTitle } from '../../../utils/timeSource'
import ExecutionDetailsModal from './ExecutionDetailsModal'
import {
  CHANNEL_NAMES,
  CHANNELS,
  Channel,
  ChannelDelivery,
  channelBadgeClass,
  channelDelivery,
  deliveryDescription,
  nobodyNotified
} from './deliveryStatus'
import { ExecutionRow } from './types'

interface AlertAuditLogTableProps {
  executions: ExecutionRow[]
  localization_context?: LocalizationContextType
  // The meal page has its own permission; without it the id is shown but not linked to a 403.
  can_view_meals?: boolean
}

interface RecipientLineProps {
  channel: Channel
  recipient: string
  delivery: ChannelDelivery
}

const RecipientLine = ({ channel, recipient, delivery }: RecipientLineProps) => (
  <div className="d-flex align-items-center gap-2 mb-1">
    <span className={channelBadgeClass(channel, delivery.status)} title={deliveryDescription(channel, delivery)}>
      {CHANNEL_NAMES[channel]}
      {/* The badge colour and its tooltip reach neither keyboard nor screen-reader users. */}
      <span className="visually-hidden">: {deliveryDescription(channel, delivery)}</span>
    </span>
    <span className="small">{recipient}</span>
  </div>
)

const AlertAuditLogTable = ({
  executions,
  localization_context: localizationContext,
  can_view_meals: canViewMeals = false
}: AlertAuditLogTableProps) => {
  const [selected, setSelected] = useState<ExecutionRow | null>(null)
  const firedAt = (execution: ExecutionRow) => formatTenantDateTime(execution.fired_at, localizationContext)

  return (
    <>
      <div className="table-responsive">
        <table className="table table-robis">
          <thead className="thead-robis">
            <tr>
              <th scope="col">Date & Time</th>
              <th scope="col">Scenario</th>
              <th scope="col">Error</th>
              <th scope="col">Notified</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {executions.map((execution) => (
              <tr key={execution.id}>
                <td className="small" title={timeSourceTitle(execution.fired_at, TIME_SOURCE.app)}>
                  {firedAt(execution)}
                </td>
                <td>
                  <span className="fw-bold">{execution.scenario_name}</span>
                  {execution.scenario_deleted && <span className="text-muted small ms-2">(deleted)</span>}
                </td>
                <td title={execution.business_error_description || undefined}>
                  <code className="fw-bold">{execution.business_error_code}</code>
                </td>
                <td>
                  {CHANNELS.map((channel) => {
                    const delivery = channelDelivery(execution, channel)

                    return delivery.recipients.map((recipient) => (
                      <RecipientLine
                        key={`${channel}-${recipient}`}
                        channel={channel}
                        recipient={recipient}
                        delivery={delivery}
                      />
                    ))
                  })}
                  {nobodyNotified(execution) && '—'}
                </td>
                <td className="text-end">
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    aria-label={`Details of ${execution.scenario_name}, fired ${firedAt(execution)}`}
                    onClick={() => setSelected(execution)}
                  >
                    Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <ExecutionDetailsModal
          execution={selected}
          localizationContext={localizationContext}
          canViewMeals={canViewMeals}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  )
}

export default AlertAuditLogTable
